
import React, { useEffect } from 'react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * PrivacyModal – a simple modal that displays a privacy policy.
 *
 * The component:
 *  - Locks body scroll while open.
 *  - Restores scroll on close or unmount.
 *  - Handles click‑outside via the backdrop.
 *  - Provides graceful fallback if `document` is unavailable (e.g. SSR).
 */
const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  // -------------------------------------------------------------------------
  // Body‑scroll handling – safely works in both client & SSR environments
  // -------------------------------------------------------------------------
  useEffect(() => {
    // Guard against environments where `document` is not defined
    if (typeof document === 'undefined') return;

    const originalOverflow = document.body.style.overflow;

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow || 'unset';
    }

    // Cleanup – always restore the original overflow value
    return () => {
      document.body.style.overflow = originalOverflow || 'unset';
    };
  }, [isOpen]);

  // If the modal is closed we render nothing
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop – clicking it closes the modal */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Modal container */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 bg-gradient-to-br from-[#0B1D3A] via-[#0F2847] to-[#0B1D3A] px-8 py-6 flex items-center justify-between border-b border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#C9A961] rounded-lg flex items-center justify-center">
              <i className="ri-lock-line text-xl text-white" aria-hidden="true"></i>
            </div>
            <h2 className="text-xl font-bold text-white">Gizlilik Politikası</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </header>

        {/* Content */}
        <div className="px-8 py-8">
          <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-[#0B1D3A] uppercase tracking-wide">
                PRIME SALES PRO
              </h3>
              <p className="text-base font-semibold text-[#0B1D3A] mt-1">
                GİZLİLİK POLİTİKASI (PRIVACY POLICY)
              </p>
            </div>

            <p>
              Prime Sales Pro olarak, web sitemizi ziyaret eden kullanıcılarımızın gizliliğini
              korumayı ve kişisel verilerini güvenli şekilde işlemeyi taahhüt ederiz. Bu Gizlilik
              Politikası, web sitemizi ziyaret ettiğinizde hangi bilgilerin toplandığını, nasıl
              kullanıldığını ve nasıl korunduğunu açıklamaktadır.
            </p>

            {/* Section 1 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">1. Veri Sorumlusu</h4>
              <p>Bu Gizlilik Politikası kapsamında kişisel verileriniz, veri sorumlusu olarak:</p>
              <p className="font-semibold text-[#0B1DA] mt-2 ml-4">Prime Sales Pro</p>
              <p className="mt-2">tarafından işlenmektedir.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 2 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">2. Toplanan Bilgiler</h4>
              <p className="mb-3">Web sitemizi kullandığınızda aşağıdaki bilgiler toplanabilir:</p>

              <p className="font-semibold text-[#0B1D3A] mb-2">Kimlik ve iletişim bilgileri:</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {['Ad ve soyad', 'E-posta adresi', 'Telefon numarası', 'Şirket adı ve unvan'].map(
                  (item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i
                        className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                        aria-hidden="true"
                      ></i>
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>

              <p className="font-semibold text-[#0B1D3A] mb-2">Teknik bilgiler:</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'IP adresi',
                  'Tarayıcı bilgisi',
                  'Cihaz bilgisi',
                  'Ziyaret edilen sayfalar',
                  'Ziyaret süresi ve etkileşim verileri',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="font-semibold text-[#0B1D3A] mb-2">Kullanıcı tarafından sağlanan bilgiler:</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'İletişim formu içerikleri',
                  'Teklif talepleri',
                  'E-posta ile gönderilen bilgiler',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 3 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">3. Bilgilerin Kullanım Amaçları</h4>
              <p className="mb-3">Toplanan bilgiler aşağıdaki amaçlarla kullanılabilir:</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Taleplerinizi yanıtlamak',
                  'Hizmetlerimiz hakkında bilgi sağlamak',
                  'Teklif hazırlamak ve iletmek',
                  'Müşteri ilişkilerini yönetmek',
                  'Hizmet kalitesini geliştirmek',
                  'Web sitesi performansını analiz etmek',
                  'Yasal yükümlülükleri yerine getirmek',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 4 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">4. Bilgilerin Korunması</h4>
              <p className="mb-3">
                Prime Sales Pro, kişisel verilerinizi korumak için uygun teknik ve idari güvenlik
                önlemlerini uygular.
              </p>
              <p className="mb-3">Bu önlemler şunları içerebilir:</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Güvenli sunucu altyapısı',
                  'Yetkisiz erişime karşı koruma',
                  'Veri erişim yetkilendirme sistemleri',
                  'Güvenlik yazılımları ve güncellemeler',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 5 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">5. Bilgilerin Paylaşılması</h4>
              <p className="mb-3">
                Kişisel verileriniz aşağıdaki durumlar dışında üçüncü kişilerle paylaşılmaz:
              </p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Hizmet sağlayıcıları (hosting, e-posta altyapısı vb.)',
                  'Yasal yükümlülükler kapsamında resmi kurumlar',
                  'Hizmet sunumunun gerektirdiği teknik sağlayıcılar',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="font-medium text-[#0B1D3A]">
                Prime Sales Pro, kişisel verilerinizi ticari amaçla satmaz veya izinsiz paylaşmaz.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 6 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">6. Çerezler (Cookies)</h4>
              <p className="mb-3">
                Web sitemiz, kullanıcı deneyimini geliştirmek için çerezler kullanabilir.
              </p>
              <p className="mb-3">Çerezler aşağıdaki amaçlarla kullanılabilir:</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Site performansını analiz etmek',
                  'Kullanıcı tercihlerini hatırlamak',
                  'Web sitesi kullanımını iyileştirmek',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>Kullanıcılar, tarayıcı ayarlarından çerezleri devre dışı bırakabilir.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 7 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">7. Veri Saklama Süresi</h4>
              <p className="mb-3">
                Kişisel verileriniz, işlenme amacı için gerekli olan süre boyunca ve ilgili yasal
                yükümlülükler çerçevesinde saklanır.
              </p>
              <p>
                Gerekli süre sonunda veriler silinir, yok edilir veya anonim hale getirilir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 8 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">8. Kullanıcı Hakları</h4>
              <p className="mb-3">Kullanıcılar aşağıdaki haklara sahiptir:</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Kişisel verilerinin işlenip işlenmediğini öğrenme',
                  'İşlenen verilere erişim talep etme',
                  'Verilerin düzeltilmesini isteme',
                  'Verilerin silinmesini talep etme',
                  'Verilerin işlenmesine itiraz etme',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>Bu talepler aşağıdaki iletişim adresine iletilebilir.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 9 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">9. Üçüncü Taraf Bağlantıları</h4>
              <p>
                Web sitemiz, üçüncü taraf web sitelerine bağlantılar içerebilir. Prime Sales Pro,
                bu sitelerin gizlilik uygulamalarından sorumlu değildir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 10 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">10. Politika Güncellemeleri</h4>
              <p className="mb-3">
                Prime Sales Pro, bu Gizlilik Politikasını gerekli gördüğü durumlarda güncelleyebilir.
              </p>
              <p>Güncel versiyon web sitesinde yayınlandığı tarihte yürürlüğe girer.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 11 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">11. İletişim</h4>
              <p className="mb-3">
                Gizlilik Politikası ile ilgili talepleriniz için bizimle iletişime geçebilirsiniz:
              </p>
              <p>
                <span className="font-semibold text-[#0B1D3A]">E-posta: </span>
                <a
                  href="mailto:info@primesalespro.com"
                  className="text-[#D4AF37] hover:underline cursor-pointer"
                >
                  info@primesalespro.com
                </a>
              </p>
              <p className="mt-2">
                <span className="font-semibold text-[#0B1D3A]">Web sitesi: </span>
                <a
                  href="https://www.primesalespro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:underline cursor-pointer"
                >
                  www.primesalespro.com
                </a>
              </p>
            </section>
          </div>

          {/* Footer Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
